import { ChangeEvent, FC, useState } from 'react';
import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useSplunkActivityLogger, useSplunkLogger } from '@resolutionlife/ui';
import { GraphQLError } from 'graphql/error/GraphQLError';

import { isEmpty } from 'ramda';
import {
  GetUserManagementDataQuery,
  TreatyDetailsFragment,
  useAddUserMutation,
} from '../../graphql/generated';
import { byAlphabeticalLabel } from '../../utils/sorting/sorting';
import { isValidEmail } from '../../utils/validation/email-validation';
import { MultiCombobox, Option } from '../multi-combobox/multi-combobox';

type AddUserModalProps = {
  isOpen: boolean;
  closeModal: () => void;
  startTimedPolling: () => void;
  roles?: GetUserManagementDataQuery['roles'];
  treaties?: TreatyDetailsFragment[];
};

type ValidationErrors = {
  name: boolean;
  email: boolean;
  roles: boolean;
};

const DEFAULT_VALIDATION_ERRORS: ValidationErrors = {
  name: false,
  email: false,
  roles: false,
};

export const AddUserModal: FC<AddUserModalProps> = ({
  isOpen,
  closeModal,
  treaties,
  roles: rolesData,
  startTimedPolling,
}) => {
  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [roles, setRoles] = useState<string[]>([]);
  const [emailNotifications, setEmailNotifications] = useState<string[]>([]);
  const [errors, setErrors] = useState<ValidationErrors>(
    DEFAULT_VALIDATION_ERRORS
  );

  const [addUser, { loading }] = useAddUserMutation({
    variables: {
      input: {
        name,
        email,
        roles,
        emailNotifications,
      },
    },
    refetchQueries: ['GetUserManagementData'],
  });

  const toast = useToast();
  const activityLogger = useSplunkActivityLogger();
  const splunkLogger = useSplunkLogger();

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setErrors({ ...errors, name: false });
    setName(event.target.value);
  };

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    setErrors({ ...errors, email: false });
    setEmail(event.target.value);
  };

  const handleRolesChange = (selectedItems: Option[]) => {
    setErrors({ ...errors, roles: false });
    const roles = selectedItems.map((item) => item.value);
    setRoles(roles);
  };

  const handleEmailNotificationsChange = (selectedItems: Option[]) => {
    const emailNotifications = selectedItems.map((item) => item.value);
    setEmailNotifications(emailNotifications);
  };

  const clearAllAndClose = () => {
    setName('');
    setEmail('');
    setErrors(DEFAULT_VALIDATION_ERRORS);
    closeModal();
  };

  const validateFields = () => {
    const errors = {
      name: !Boolean(name),
      email: !Boolean(email) || !isValidEmail(email),
      roles: isEmpty(roles),
    };

    setErrors(errors);

    return {
      hasAtLeastOneError: Object.values(errors).some(Boolean),
    };
  };

  const handleAddUser = () => {
    (async () => {
      const { hasAtLeastOneError } = validateFields();
      if (hasAtLeastOneError) return;
      try {
        const { data } = await addUser();
        if (data) {
          // Need to poll because Auth0 takes a few seconds to update
          startTimedPolling();

          activityLogger.logUserAdd({
            actionType: 'add',
            targetRole: roles.join(', '),
          });

          toast({
            title: `Added ${data.addUser.name}`,
            status: 'success',
            position: 'top',
          });
          clearAllAndClose();
        }
      } catch (error) {
        const isUserAlreadyExistsError = (error as any)?.graphQLErrors.some(
          (graphQLError: GraphQLError) =>
            graphQLError.extensions?.code === 'USER_ALREADY_EXISTS'
        );
        const isEmailDomainNotAllowedError = (error as any)?.graphQLErrors.some(
          (graphQLError: GraphQLError) =>
            graphQLError.extensions?.code === 'EMAIL_DOMAIN_NOT_ALLOWED'
        );

        if (isUserAlreadyExistsError) {
          toast({
            title: `Unable to add user`,
            description: `A user with this email already exists`,
            status: 'error',
            position: 'top',
          });
          splunkLogger.error('Add user failed', { eventCategory: 'user_management_error', errorCode: 'USER_ALREADY_EXISTS', email: email.split('@')[1] });
        } else if (isEmailDomainNotAllowedError) {
          toast({
            title: `Unable to add user`,
            description: `The email domain is not allowed for external users.`,
            status: 'error',
            position: 'top',
          });
          splunkLogger.error('Add user failed', { eventCategory: 'user_management_error', errorCode: 'EMAIL_DOMAIN_NOT_ALLOWED', email: email.split('@')[1] });
        } else if (error) {
          toast({
            title: `Unable to add user`,
            description: 'Please try again',
            status: 'error',
            position: 'top',
          });
          splunkLogger.error('Add user failed', { eventCategory: 'user_management_error', errorMessage: String(error) });
        }
      }
    })();
  };

  const roleOptions = rolesData
    ? rolesData.map((role) => ({
        value: role.id,
        label: role.id,
      }))
    : [];

  const sortedRoleOptions = [...roleOptions];
  sortedRoleOptions.sort(byAlphabeticalLabel);

  const emailNotificationOptions = treaties
    ? treaties.map((treaty) => ({
        value: treaty.cedant,
        label: treaty.name,
      }))
    : [];

  return (
    <Modal
      isOpen={isOpen}
      onClose={clearAllAndClose}
      size="2xl"
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent data-testid="add-user-modal" p={2}>
        <ModalHeader>
          <Text px={2} fontSize="2xl">
            Add User
          </Text>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody px={8} pt={4} pb={8}>
          <Stack as="form" spacing={8}>
            <FormControl isInvalid={errors.name} isRequired>
              <FormLabel>Name</FormLabel>
              <Input
                onChange={handleNameChange}
                value={name}
                data-testid="name-input"
              />
              {errors.name && (
                <FormErrorMessage data-testid="name-error-msg">
                  Please provide a name
                </FormErrorMessage>
              )}
            </FormControl>

            <FormControl isInvalid={errors.email} isRequired>
              <FormLabel>Email</FormLabel>
              <Input
                onChange={handleEmailChange}
                value={email}
                type="email"
                data-testid="email-input"
              />
              {errors.email && (
                <FormErrorMessage data-testid="email-error-msg">
                  Please provide a valid email
                </FormErrorMessage>
              )}
            </FormControl>

            <MultiCombobox
              label="Assigned Roles"
              options={sortedRoleOptions}
              onChange={handleRolesChange}
              isInvalid={errors.roles}
              errorMessage="Please assign at least one role"
              isRequired
              testId="roles-multi-combobox"
            />

            <MultiCombobox
              label="Email Notifications"
              options={emailNotificationOptions}
              onChange={handleEmailNotificationsChange}
              testId="email-notifications-multi-combobox"
            />
          </Stack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={4}>
            <Button onClick={clearAllAndClose}>Cancel</Button>
            <Button
              onClick={handleAddUser}
              isLoading={loading}
              colorScheme="green"
              type="submit"
              data-testid="confirm-add-user-button"
            >
              Add User
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
